import { useState } from 'react';
import { Box, Stack, Switch } from '@mantine/core';
import { ROKU_PREVIEW_GEOMETRY as G } from '@/lib/constants/appFactory';
import { Missing, Preview, TrueSizeStrip, TvFrame } from './TvDevicePreviews';

interface RokuScreenPreviewsProps {
  /** name → URL of the set's files (BrandKitUtils.previewFileMap). */
  files: Record<string, string>;
  /** Width of the scaled full-screen frame (the splash), px (the frame is 16:9). */
  frameWidth?: number;
}

const PLACEHOLDER = '#2a2f3a';

/**
 * Roku parity (jvm notes/ott/tv-graphics/design_roku_parity_20260928.md R6):
 * the Roku set drawn where Roku shows it — the channel poster among the
 * installed apps at its on-screen size (one CSS pixel per pixel of a 1080p
 * screen since 3.42.0; approximate: Roku publishes the 540 x 405 art, not the
 * tile), the Channel Store poster, and the full-screen splash, which stays a
 * scaled frame. Pure CSS, like TvDevicePreviews.
 */
export function RokuScreenPreviews({ files, frameWidth = 560 }: RokuScreenPreviewsProps) {
  const [focused, setFocused] = useState(true);

  return (
    <Stack gap="lg">
      <Preview title="Roku — Home screen" note="Your channel poster in the grid of installed apps.">
        <TrueSizeStrip gap={G.gapPx}>
          <Poster w={G.tilePx.w} h={G.tilePx.h} src={files['icon_fhd.png']} focused={focused} missing />
          <Poster w={G.tilePx.w} h={G.tilePx.h} />
          <Poster w={G.tilePx.w} h={G.tilePx.h} />
        </TrueSizeStrip>
        <Switch size="xs" mt={6} label="Focused" checked={focused} onChange={(e) => setFocused(e.currentTarget.checked)} />
      </Preview>

      <Preview title="Roku Channel Store" note="The poster on your store page.">
        <Poster w={270} h={202} src={files['store_poster.png']} alt="Store poster" missing />
      </Preview>

      <Preview title="Splash" note="What a viewer sees while the app opens (the whole screen, scaled down).">
        <TvFrame width={frameWidth} ground={files['splash_fhd.jpg']}>
          {!files['splash_fhd.jpg'] && <Missing />}
        </TvFrame>
      </Preview>
    </Stack>
  );
}

function Poster({
  w,
  h,
  src,
  alt = 'Roku channel poster',
  focused,
  missing,
}: {
  w: number;
  h: number;
  src?: string;
  alt?: string;
  focused?: boolean;
  /** Ours: says so when the set has no such file (a neighbour stays a blank tile). */
  missing?: boolean;
}) {
  return (
    <Box
      data-testid={src ? 'roku-poster' : undefined}
      style={{
        width: w,
        height: h,
        flex: 'none',
        borderRadius: 6,
        overflow: 'hidden',
        background: PLACEHOLDER,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: focused ? 'scale(1.06)' : undefined,
        boxShadow: focused ? '0 0 0 3px #ffffff' : undefined,
      }}
    >
      {src ? <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : missing ? <Missing /> : null}
    </Box>
  );
}
